// Debounced connectivity watcher — ported from sai-infra's
// features/offline/connectivity.ts, unchanged in spirit.
//
// `navigator.onLine` is only ever a HINT to retry, never the source of truth
// for "are we offline" — it reports "online" for a phone associated with a
// captive-portal hall wifi that routes nowhere (the single most common
// tradeshow network). The authority on offline-vs-logged-out is what `fetch`
// actually did, in lib/api/http.ts and lib/auth/session.ts. This module exists
// only to give the sync engine a cheap "connectivity probably returned — try a
// drain" edge, debounced so an Android 4G handoff (which fires offline+online
// back-to-back in 1-2s) doesn't whipsaw the sync indicator.

type Listener = (online: boolean) => void;

export interface ConnectivityOptions {
  /** How long a transition must hold before subscribers are notified. */
  holdMs?: number;
}

class ConnectivityWatcher {
  private current: boolean;
  private pendingTarget: boolean | null = null;
  private pendingTimer: number | null = null;
  private listeners = new Set<Listener>();
  private readonly holdMs: number;
  private boundOnline = (): void => this.handle(true);
  private boundOffline = (): void => this.handle(false);
  private started = false;

  constructor(opts: ConnectivityOptions = {}) {
    this.holdMs = opts.holdMs ?? 1500;
    this.current = typeof navigator !== 'undefined' ? navigator.onLine : true;
  }

  start(): void {
    if (this.started || typeof window === 'undefined') return;
    window.addEventListener('online', this.boundOnline);
    window.addEventListener('offline', this.boundOffline);
    this.started = true;
  }

  stop(): void {
    if (!this.started || typeof window === 'undefined') return;
    window.removeEventListener('online', this.boundOnline);
    window.removeEventListener('offline', this.boundOffline);
    if (this.pendingTimer !== null) {
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    this.started = false;
  }

  isOnline(): boolean {
    return this.current;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private handle(target: boolean): void {
    if (target === this.current && this.pendingTimer === null) return;
    if (target === this.pendingTarget) return;
    if (this.pendingTimer !== null && typeof window !== 'undefined') {
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    this.pendingTarget = target;
    if (typeof window === 'undefined') {
      this.commit(target);
      return;
    }
    this.pendingTimer = window.setTimeout(() => {
      this.pendingTimer = null;
      this.commit(target);
    }, this.holdMs);
  }

  private commit(target: boolean): void {
    this.pendingTarget = null;
    if (target === this.current) {
      // A flap that ended where it started — handle() only schedules a commit
      // for a transition, so reaching here means the network wobbled and
      // settled back. Returning silently was a data-loss path: the wobble is
      // enough to fail in-flight requests and stop the sync drain, but this
      // notification is the drain's main restart trigger, so the flap
      // suppressed the one event that would have recovered from it.
      //
      // Online only. Subscribers treat `true` as a cheap idempotent "try
      // again"; a redundant `false` would flip the UI to an offline state that
      // is not real.
      if (target) this.notify(true);
      return;
    }
    this.current = target;
    this.notify(target);
  }

  private notify(online: boolean): void {
    for (const fn of this.listeners) fn(online);
  }
}

/** Module singleton — `start()` once from startOffline(); others subscribe. */
export const connectivity = new ConnectivityWatcher();

export function isOnlineHint(): boolean {
  return connectivity.isOnline();
}

export function subscribeConnectivity(fn: Listener): () => void {
  return connectivity.subscribe(fn);
}
